import { createRoute, Link, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, Card, EmptyState, Field, Input, Screen, Skeleton } from "../../shared/ui/index.js";
import { Stagger, Item } from "../../shared/motion.js";
import { useToast } from "../../shared/toast.js";
import { useSession } from "../auth/auth.api.js";
import { Shell } from "../../app/Shell.js";
import { requireAuth } from "../../app/guards.js";
import { rootRoute } from "../../app/root-route.js";
import { useCreateWedding, useWeddings } from "./weddings.api.js";

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  beforeLoad: requireAuth(),
  component: () => (
    <Shell>
      <WeddingsScreen />
    </Shell>
  ),
});

function WeddingsScreen() {
  const { data: user } = useSession();
  const { data, isLoading } = useWeddings();

  if (isLoading || !data) {
    return (
      <Screen title="Mariage">
        <Skeleton count={2} className="h-16" />
      </Screen>
    );
  }

  const isAdmin = user?.role === "admin";

  // Client : 1 compte = 1 mariage. On l'emmène directement dessus.
  if (!isAdmin) {
    if (data.items.length >= 1) {
      return <Navigate to="/app/weddings/$id" params={{ id: data.items[0]!.id }} replace />;
    }
    return <FirstWedding />;
  }

  // Admin : liste de tous les mariages.
  return (
    <Screen title="Mariages">
      {data.items.length > 0 ? (
        <Stagger className="flex flex-col gap-2">
          {data.items.map((w) => (
            <Item key={w.id}>
              <Link
                to="/app/weddings/$id"
                params={{ id: w.id }}
                className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm transition hover:border-stone-300 hover:shadow"
              >
                <span className="font-serif text-lg text-stone-900">
                  {w.partner1Name} &amp; {w.partner2Name}
                </span>
                <span className="text-stone-300">→</span>
              </Link>
            </Item>
          ))}
        </Stagger>
      ) : (
        <EmptyState>Aucun mariage. Les clients créent le leur à la première connexion.</EmptyState>
      )}
    </Screen>
  );
}

function FirstWedding() {
  const create = useCreateWedding();
  const toast = useToast();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");

  return (
    <Screen title="Votre mariage">
      <Card>
        <h2 className="font-serif text-lg text-stone-900">Créons votre espace</h2>
        <p className="mt-1 text-sm text-stone-500">
          Renseignez les prénoms des mariés. Vous pourrez ensuite ajouter autant d'événements
          que vous voulez (cérémonie, réception…).
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { partner1Name: p1, partner2Name: p2 },
              { onError: () => toast("Impossible de créer le mariage", "err") },
            );
          }}
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
        >
          <Field label="Partenaire 1">
            <Input required value={p1} onChange={(e) => setP1(e.target.value)} />
          </Field>
          <Field label="Partenaire 2">
            <Input required value={p2} onChange={(e) => setP2(e.target.value)} />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Création…" : "Créer"}
          </Button>
        </form>
      </Card>
    </Screen>
  );
}
